/**
 * @fileoverview Unified audio system facade.
 * Coordinates background music and sound effects services.
 */

import { KonvaEventReal } from '../konva-extensions.js';
import { BackgroundMusicService } from './BackgroundMusicService.js';
import { SoundEffectsService } from './SoundEffectsService.js';

/**
 * AudioSystem facade that coordinates all audio services.
 * Provides a single entry point for audio management.
 */
export class AudioSystem {
    private backgroundMusic: BackgroundMusicService;
    private soundEffects: SoundEffectsService;
    
    constructor() {
        this.backgroundMusic = new BackgroundMusicService();
        this.soundEffects = new SoundEffectsService();
        
        // Link services for coordination
        this.soundEffects.setBackgroundMusic(this.backgroundMusic);
    }
    
    /**
     * Initialize all audio services.
     */
    init(): void {
        this.backgroundMusic.init();
        this.soundEffects.init();
    }
    
    /**
     * Get the background music service.
     * @returns {BackgroundMusicService} Background music service
     */
    getBackgroundMusic(): BackgroundMusicService {
        return this.backgroundMusic;
    }
    
    /**
     * Get the sound effects service.
     * @returns {SoundEffectsService} Sound effects service
     */
    getSoundEffects(): SoundEffectsService {
        return this.soundEffects;
    }
    
    /**
     * Try to unlock all audio from a trusted user event.
     * Unlocks both background music and sound effects.
     * @param {any} evt - The originating user event
     * @returns {boolean} True if unlock was attempted
     */
    tryUnlockFromEvent(evt: KonvaEventReal): boolean {
        const bgUnlocked = this.backgroundMusic.tryUnlockFromEvent(evt);
        const sfxUnlocked = this.soundEffects.tryUnlockFromEvent(evt);
        return bgUnlocked || sfxUnlocked;
    }
    
    /**
     * Toggle mute for background music.
     * @returns {boolean} New mute state
     */
    toggleBackgroundMute(): boolean {
        return this.backgroundMusic.toggleMute();
    }
    
    /**
     * Toggle mute for sound effects.
     * @returns {boolean} New mute state
     */
    toggleSoundEffectsMute(): boolean {
        return this.soundEffects.toggleMute();
    }
    
    /**
     * Toggle mute for all audio (both music and effects).
     * @returns {boolean} New mute state (returns music mute state)
     */
    toggleMuteAll(): boolean {
        this.soundEffects.toggleMute();
        return this.backgroundMusic.toggleMute();
    }
    
    /**
     * Set volume for background music.
     * @param {number} volume - Volume level (0.0 to 1.0)
     */
    setBackgroundVolume(volume: number): void {
        this.backgroundMusic.setVolume(volume);
    }
    
    /**
     * Set volume for sound effects.
     * @param {number} volume - Volume level (0.0 to 1.0)
     */
    setSoundEffectsVolume(volume: number): void {
        this.soundEffects.setVolume(volume);
    }
    
    /**
     * Check if background music is playing.
     * @returns {boolean} True if playing
     */
    isBackgroundMusicPlaying(): boolean {
        return this.backgroundMusic.isPlaying();
    }
    
    /**
     * Check if background music is muted.
     * @returns {boolean} True if muted
     */
    isBackgroundMusicMuted(): boolean {
        return this.backgroundMusic.isMuted();
    }
    
    /**
     * Check if sound effects are muted.
     * @returns {boolean} True if muted
     */
    areSoundEffectsMuted(): boolean {
        return this.soundEffects.isMuted();
    }
}
